import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Download, Calendar } from "lucide-react";

const BURL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReports() {
  const { api, isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [billable, setBillable] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [utilization, setUtilization] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!isAdmin) navigate("/login"); }, [isAdmin]);

  const loadReport = async (type) => {
    setLoading(true);
    try {
      let url = `/admin/reports/${type}?`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      const res = await api("get", url);
      if (type === "billable") setBillable(res.data);
      else if (type === "revenue") setRevenue(res.data);
      else if (type === "utilization") setUtilization(res.data);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const exportReport = (type, format) => {
    let url = `${BURL}/api/admin/reports/export?report_type=${type}&format=${format}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    window.open(url + `&token=${token}`, "_blank");
  };

  const CHART_COLORS = ["#0A0A0A", "#404040", "#737373", "#A3A3A3", "#D4D4D4"];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 animate-fade-in" data-testid="admin-reports">
      <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2">Reports</h1>
      <p className="text-muted-foreground mb-8">Generate and export business reports</p>

      {/* Date Range */}
      <Card className="p-5 mb-8 border border-border" data-testid="date-range-card">
        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div>
            <Label>Start Date</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="report-start-date" />
          </div>
          <div>
            <Label>End Date</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="report-end-date" />
          </div>
          <Button onClick={() => { loadReport("billable"); loadReport("revenue"); loadReport("utilization"); }} className="bg-foreground text-background rounded-none" data-testid="generate-reports-btn">
            <Calendar className="h-4 w-4 mr-2" />Generate Reports
          </Button>
        </div>
      </Card>

      <Tabs defaultValue="billable">
        <TabsList className="mb-6">
          <TabsTrigger value="billable" data-testid="tab-billable">Billable</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="utilization" data-testid="tab-utilization">Utilization</TabsTrigger>
        </TabsList>

        {/* Billable Report */}
        <TabsContent value="billable">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-xl">Billable Bookings</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => exportReport("billable", "csv")} data-testid="export-billable-csv">
                <Download className="h-3 w-3 mr-1" />CSV
              </Button>
              <Button size="sm" variant="outline" onClick={() => exportReport("billable", "xlsx")} data-testid="export-billable-xlsx">
                <Download className="h-3 w-3 mr-1" />Excel
              </Button>
            </div>
          </div>
          <div className="rounded-lg border overflow-x-auto" data-testid="billable-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Space</TableHead>
                <TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Duration</TableHead>
                <TableHead>Amount</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {billable.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{b.name}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{b.user_type}</Badge></TableCell>
                    <TableCell>{b.space}</TableCell>
                    <TableCell className="font-mono text-xs">{b.date}</TableCell>
                    <TableCell className="font-mono text-xs">{b.time}</TableCell>
                    <TableCell>{b.duration_hours}h</TableCell>
                    <TableCell className="font-medium">XCG {b.amount?.toFixed(2)}</TableCell>
                    <TableCell><Badge className={b.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{b.payment_status}</Badge></TableCell>
                    <TableCell><Badge className="bg-muted text-muted-foreground">{b.booking_status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {billable.length === 0 && <div className="p-8 text-center text-muted-foreground">No data. Click Generate Reports.</div>}
          </div>
        </TabsContent>

        {/* Revenue Report */}
        <TabsContent value="revenue">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-xl">Revenue Report</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => exportReport("revenue", "csv")} data-testid="export-revenue-csv">
                <Download className="h-3 w-3 mr-1" />CSV
              </Button>
              <Button size="sm" variant="outline" onClick={() => exportReport("revenue", "xlsx")} data-testid="export-revenue-xlsx">
                <Download className="h-3 w-3 mr-1" />Excel
              </Button>
            </div>
          </div>
          {revenue ? (
            <div className="space-y-6">
              <Card className="p-5 border border-border" data-testid="revenue-summary">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div><p className="text-sm text-muted-foreground">Total Revenue</p><p className="font-heading font-bold text-2xl text-primary">XCG {revenue.total_revenue?.toFixed(2)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Total Bookings</p><p className="font-heading font-bold text-2xl">{revenue.booking_count}</p></div>
                </div>
              </Card>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-5 border border-border" data-testid="revenue-by-space-chart">
                  <h3 className="font-medium mb-4">Revenue by Space</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={revenue.by_space}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="space" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Card className="p-5 border border-border" data-testid="revenue-by-type-chart">
                  <h3 className="font-medium mb-4">Revenue by Type</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={revenue.by_type} dataKey="revenue" nameKey="type" cx="50%" cy="50%" outerRadius={80} label={({ type, revenue }) => `${type}: XCG ${revenue}`}>
                        {revenue.by_type?.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            </div>
          ) : <div className="p-8 text-center text-muted-foreground">No data. Click Generate Reports.</div>}
        </TabsContent>

        {/* Utilization Report */}
        <TabsContent value="utilization">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-xl">Utilization Report</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => exportReport("utilization", "csv")} data-testid="export-utilization-csv">
                <Download className="h-3 w-3 mr-1" />CSV
              </Button>
              <Button size="sm" variant="outline" onClick={() => exportReport("utilization", "xlsx")} data-testid="export-utilization-xlsx">
                <Download className="h-3 w-3 mr-1" />Excel
              </Button>
            </div>
          </div>
          {utilization ? (
            <div className="space-y-6">
              <Card className="p-5 border border-border" data-testid="utilization-summary">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div><p className="text-sm text-muted-foreground">Period</p><p className="font-medium">{utilization.period?.start} to {utilization.period?.end}</p></div>
                  <div><p className="text-sm text-muted-foreground">Total Bookings</p><p className="font-heading font-bold text-2xl">{utilization.total_bookings}</p></div>
                </div>
              </Card>
              <div className="rounded-lg border overflow-x-auto" data-testid="utilization-table">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Space</TableHead><TableHead>Bookings</TableHead><TableHead>Booked Hours</TableHead><TableHead>Occupancy</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {utilization.spaces?.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{s.space_name}</TableCell>
                        <TableCell>{s.total_bookings}</TableCell>
                        <TableCell>{s.total_booked_hours}h</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(s.occupancy_pct, 100)}%` }} />
                            </div>
                            <span className="text-sm font-medium">{s.occupancy_pct}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {utilization.peak_hours?.length > 0 && (
                <Card className="p-5 border border-border" data-testid="peak-hours">
                  <h3 className="font-medium mb-4">Peak Hours</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={utilization.peak_hours}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="bookings" fill="#404040" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          ) : <div className="p-8 text-center text-muted-foreground">No data. Click Generate Reports.</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
